import React, { useCallback, useState } from 'react';
import { Loader2, Wand2, Film, Check } from 'lucide-react';
import { TYPE } from '../../constants/typeScale';
import AutoTextarea from '../ui/AutoTextarea';
import { planEpisodes, type PlannedEpisode } from '../../services/studioService';

/**
 * THE RUN — how many episodes, and what each one is for.
 *
 * Owner 2026-08-25: *"how we plan to build up, climax, cliffhanger, how we plan out the episodes, how many
 * episodes (user should project)."* Without this a creator cannot say "this is a twelve-episode show", so
 * nothing knows what episode 7 exists to do, and every episode is written as if it were the only one.
 *
 * PLANNING IS NOT MAKING, and the page has to say so. A row here mints nothing: it becomes a real episode when
 * the creator opens it. Sixty empty documents would make the explorer unusable and — worse — imply a spend
 * commitment that does not exist. So the header counts `made / planned`, from the very first row, rather than
 * explaining the difference in a sentence nobody reads.
 *
 * THE TARGET DEFAULTS TO 12. The market's commercial unit is 60-100 episodes and that is stated here, because
 * it is true and it is what the format rewards — but defaulting a first project to sixty produces an
 * intimidating page and a plan nobody follows. Ambition is offered, not imposed.
 */

export interface ProjectEpisodesProps {
  target: number;
  onTargetChange: (n: number) => void;
  plan: PlannedEpisode[];
  onPlanChange: (plan: PlannedEpisode[]) => void;
  /** The project's plot — what the ladder is drafted from. */
  plot: string;
  seriesId?: string | null;
  onNeedsTopUp?: () => void;
  /** Open a planned row as a real episode. Absent while the project has no id to hang one off. */
  onOpenEpisode?: (row: PlannedEpisode) => void;
}

const ProjectEpisodes: React.FC<ProjectEpisodesProps> = ({
  target, onTargetChange, plan, onPlanChange, plot, seriesId, onNeedsTopUp, onOpenEpisode,
}) => {
  const [drafting, setDrafting] = useState(false);
  const made = plan.filter((r) => r.storyboard_id).length;

  const draft = useCallback(async () => {
    if (drafting) return;
    setDrafting(true);
    try {
      const r = await planEpisodes(plot, target, seriesId);
      if (r.needsTopUp) { onNeedsTopUp?.(); return; }
      if (!r.plan?.length) return;
      /**
       * MERGE BY EPISODE NUMBER, never replace. A creator who has already opened episode 1 — or rewritten its
       * beat — must not lose either to a re-plan. A row that has become a real episode is left completely
       * alone: its storyboard is the truth by then, not the plan that preceded it.
       */
      const byN = new Map(plan.map((row) => [row.n, row]));
      const merged = r.plan.map((row) => {
        const existing = byN.get(row.n);
        if (existing?.storyboard_id) return existing;
        return { ...row, ...(existing ? { storyboard_id: existing.storyboard_id } : {}) };
      });
      // Anything the creator planned beyond the new target survives rather than being truncated away.
      for (const row of plan) if (!merged.some((m) => m.n === row.n)) merged.push(row);
      onPlanChange(merged.sort((a, b) => a.n - b.n));
    } finally {
      setDrafting(false);
    }
  }, [drafting, plot, target, seriesId, plan, onPlanChange, onNeedsTopUp]);

  const setRow = (n: number, patch: Partial<PlannedEpisode>) =>
    onPlanChange(plan.map((r) => (r.n === n ? { ...r, ...patch } : r)));

  return (
    <section className="mt-6" aria-label="Episodes">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className={`${TYPE.label} text-secondary-400 dark:text-secondary-500`}>The run</span>
        <span className={`min-w-0 flex-1 ${TYPE.meta} text-secondary-400`}>
          {plan.length
            ? `${made} made of ${plan.length} planned — a row is a plan until you open it.`
            : 'Micro-dramas run 60 to 100 episodes commercially. Start with what you can actually finish.'}
        </span>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <label className="inline-flex items-center gap-2">
          <span className={`${TYPE.meta} text-secondary-400`}>Episodes</span>
          <input
            type="number"
            min={1}
            max={120}
            value={target}
            onChange={(e) => onTargetChange(Math.max(1, Math.min(120, Number(e.target.value) || 1)))}
            data-testid="episode-target"
            className={`w-16 rounded-lg border border-secondary-200 bg-white px-2 py-1 ${TYPE.control} tabular-nums
                       text-secondary-900 outline-none focus:border-secondary-400 dark:border-secondary-600
                       dark:bg-secondary-900 dark:text-white`}
          />
        </label>
        <button
          type="button"
          onClick={() => void draft()}
          disabled={drafting || !plot.trim()}
          data-testid="plan-episodes"
          className={`inline-flex min-h-[34px] items-center gap-2 rounded-lg border border-secondary-200 px-3
                     ${TYPE.control} font-medium text-secondary-700 transition-colors hover:border-secondary-400
                     disabled:opacity-40 dark:border-secondary-600 dark:text-secondary-200`}
        >
          {drafting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
          {drafting ? 'Planning the run…' : plan.length ? 'Re-plan the run' : 'Plan the run'}
        </button>
        {!plot.trim() && (
          <span className={`${TYPE.meta} text-secondary-400`}>Write the plot first — the run is built from it.</span>
        )}
      </div>

      {plan.length > 0 && (
        <ol className="mt-3 space-y-2" data-testid="episode-plan">
          {plan.map((row) => (
            <li key={row.n} className="rounded-lg border border-secondary-200 p-3 dark:border-secondary-600">
              <div className="flex items-center gap-2">
                <span className={`shrink-0 font-mono ${TYPE.meta} text-secondary-400`}>{row.n}</span>
                <input
                  value={row.working_title || ''}
                  onChange={(e) => setRow(row.n, { working_title: e.target.value })}
                  placeholder={`Episode ${row.n}`}
                  aria-label={`Episode ${row.n} title`}
                  data-testid={`plan-title-${row.n}`}
                  className={`min-w-0 flex-1 rounded border-0 bg-transparent p-0 ${TYPE.sectionTitle}
                             text-secondary-900 outline-none placeholder:font-normal
                             placeholder:text-secondary-400 dark:text-white`}
                />
                {row.storyboard_id ? (
                  <span className={`inline-flex shrink-0 items-center gap-1 ${TYPE.meta} text-secondary-500 dark:text-secondary-400`}>
                    <Check className="h-3 w-3" /> Made
                  </span>
                ) : onOpenEpisode ? (
                  <button
                    type="button"
                    onClick={() => onOpenEpisode(row)}
                    data-testid={`open-episode-${row.n}`}
                    className={`inline-flex shrink-0 items-center gap-1 rounded-lg border border-secondary-200 px-2 py-1
                               ${TYPE.meta} text-secondary-600 transition-colors hover:border-secondary-400
                               dark:border-secondary-600 dark:text-secondary-300`}
                  >
                    <Film className="h-3 w-3" /> Write it
                  </button>
                ) : null}
              </div>
              <AutoTextarea
                value={row.beat || ''}
                onChange={(v) => setRow(row.n, { beat: v })}
                minRows={1}
                placeholder="The one thing that escalates in this episode."
                aria-label={`Episode ${row.n} beat`}
                data-testid={`plan-beat-${row.n}`}
                className={`mt-2 block w-full rounded-lg border border-secondary-200 bg-white px-3 py-1.5
                           ${TYPE.control} leading-relaxed text-secondary-900 outline-none
                           placeholder:text-secondary-400 focus:border-secondary-400 dark:border-secondary-600
                           dark:bg-secondary-900 dark:text-white`}
              />
              <label className="mt-1.5 block">
                <span className={`${TYPE.meta} text-secondary-400`}>Ends on</span>
                <AutoTextarea
                  value={row.cliffhanger || ''}
                  onChange={(v) => setRow(row.n, { cliffhanger: v })}
                  minRows={1}
                  placeholder="Unresolved — this is why anyone taps the next one."
                  aria-label={`Episode ${row.n} cliffhanger`}
                  data-testid={`plan-cliff-${row.n}`}
                  className={`mt-0.5 block w-full rounded-lg border border-secondary-200 bg-white px-3 py-1.5
                             ${TYPE.control} leading-relaxed text-secondary-900 outline-none
                             placeholder:text-secondary-400 focus:border-secondary-400 dark:border-secondary-600
                             dark:bg-secondary-900 dark:text-white`}
                />
              </label>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
};

export default ProjectEpisodes;
